import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Database, FileJson, Download, Upload, FileSpreadsheet, Loader2, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { downloadCompagnieTemplate, downloadCompagnieExcelTemplate, parseCompagnieFile, companyName } from "../../lib/compagnieImport";
import CompagniePreview from "../../components/CompagniePreview";
import { toast } from "sonner";
import { TabelleInvaliditaCard } from "../../components/TabelleInvaliditaCard";

function StudiSelector({ tenants, all, setAll, selected, setSelected, testPrefix }) {
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-mono">Studi destinazione</p>
      <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid={`${testPrefix}-all-studi`}>
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
        Tutti gli studi ({tenants.length})
      </label>
      {!all && (
        <div className="max-h-40 overflow-y-auto space-y-1 pl-1 border-t border-slate-800 pt-2">
          {tenants.map((t) => (
            <label key={t.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid={`${testPrefix}-studio-${t.id}`}>
              <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} />
              {t.studio_name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Importazione() {
  const [tenants, setTenants] = useState([]);
  const [tinv, setTinv] = useState([]);
  const loadTinv = () => api.get("/admin/tabelle-invalidita").then(({ data }) => setTinv(data || [])).catch(() => {});
  useEffect(() => { loadTinv(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/tenants");
        setTenants(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  // --- Import compagnie JSON ---
  const [companies, setCompanies] = useState(null);
  const [fileName, setFileName] = useState("");
  const [strategy, setStrategy] = useState("skip");
  const [all, setAll] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const resetJson = () => { setCompanies(null); setFileName(""); setStrategy("skip"); setAll(true); setSelected([]); setReport(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setReport(null);
    try {
      const arr = (await parseCompagnieFile(file)).filter((c) => companyName(c));
      if (arr.length === 0) { toast.error("Il file non contiene compagnie valide (manca il nome)."); return; }
      setFileName(file.name); setCompanies(arr);
    } catch (err) { toast.error(err.message || "File non valido"); }
  };

  const doImport = async () => {
    if (!companies) return;
    if (!all && selected.length === 0) { toast.error("Seleziona almeno uno studio"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/compagnie/import-json", { compagnie: companies, strategy, tenant_ids: all ? "all" : selected });
      setReport(data); toast.success("Importazione completata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // --- Import uffici reclami PDF ---
  const [pdfName, setPdfName] = useState("");
  const [recs, setRecs] = useState(null); // [{...record, match, score, candidates}]
  const [compList, setCompList] = useState([]);
  const [mapping, setMapping] = useState({}); // idx -> compagnia_name ("" = ignora)
  const [pAll, setPAll] = useState(true);
  const [pSelected, setPSelected] = useState([]);
  const [pBusy, setPBusy] = useState(false);
  const [pParsing, setPParsing] = useState(false);
  const [pReport, setPReport] = useState(null);

  const resetPdf = () => { setPdfName(""); setRecs(null); setCompList([]); setMapping({}); setPAll(true); setPSelected([]); setPReport(null); };

  const onPdf = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setPReport(null); setRecs(null); setPParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uffici-reclami/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdfName(file.name);
      setRecs(data.records);
      setCompList(data.compagnie || []);
      const m = {};
      data.records.forEach((r, i) => { m[i] = r.match || ""; });
      setMapping(m);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "PDF non valido"); }
    finally { setPParsing(false); }
  };

  const doApplyPdf = async () => {
    if (!recs) return;
    if (!pAll && pSelected.length === 0) { toast.error("Seleziona almeno uno studio"); return; }
    const items = recs.map((r, i) => ({ record: r, compagnia_name: mapping[i] || "" })).filter((it) => it.compagnia_name);
    if (items.length === 0) { toast.error("Nessuna compagnia abbinata da aggiornare."); return; }
    setPBusy(true);
    try {
      const { data } = await api.post("/admin/uffici-reclami/apply", { items, tenant_ids: pAll ? "all" : pSelected });
      setPReport(data); toast.success("Uffici reclami aggiornati");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPBusy(false); }
  };

  const matchedCount = recs ? recs.filter((_, i) => mapping[i]).length : 0;
  const unmatchedCount = recs ? recs.length - matchedCount : 0;

  return (
    <div data-testid="admin-importazione">
      <PageHeader title="Importazione Dati"
        subtitle="Strumenti di importazione dati riservati al SuperAdmin: compagnie assicurative e uffici reclami."
        icon={Database} />

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {/* --- Compagnie JSON --- */}
        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-5 space-y-4" data-testid="import-compagnie-card">
          <div className="flex items-center gap-2 text-slate-100 font-head font-semibold">
            <FileJson className="h-5 w-5 text-amber-400" /> Importa Compagnie da JSON
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={downloadCompagnieTemplate} data-testid="admin-download-template" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <Download className="h-4 w-4 mr-1.5" /> Template JSON
            </Button>
            <Button type="button" variant="outline" onClick={downloadCompagnieExcelTemplate} data-testid="admin-download-excel-template" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Template Excel
            </Button>
          </div>
          <label className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-600 bg-slate-900/40 px-4 py-6 cursor-pointer hover:border-amber-500/50 transition-colors" data-testid="admin-import-file-label">
            <Upload className="h-6 w-6 text-slate-400" />
            <span className="text-sm text-slate-300">{fileName || "Seleziona un file .json, .xlsx o .csv"}</span>
            <input type="file" accept=".json,application/json,.csv,text/csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="admin-import-file-input" />
          </label>
          {companies && !report && (
            <>
              <CompagniePreview companies={companies} />
              <p className="text-sm text-slate-200"><strong>{companies.length}</strong> compagnie nel file.</p>
              <StudiSelector tenants={tenants} all={all} setAll={setAll} selected={selected} setSelected={setSelected} testPrefix="imp" />
              <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                <p className="text-xs text-slate-400">Compagnie già esistenti in uno studio?</p>
                <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="admin-strategy-skip">
                  <input type="radio" name="admin-strategy" checked={strategy === "skip"} onChange={() => setStrategy("skip")} /> Salta le esistenti
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="admin-strategy-overwrite">
                  <input type="radio" name="admin-strategy" checked={strategy === "overwrite"} onChange={() => setStrategy("overwrite")} /> Sovrascrivi le esistenti
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetJson} className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
                <Button type="button" onClick={doImport} disabled={busy} data-testid="admin-import-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Importa"}
                </Button>
              </div>
            </>
          )}
          {report && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm space-y-2" data-testid="admin-import-report">
              <p className="text-emerald-300 font-semibold">Report</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-200">
                <span>Nuove: <strong>{report.totale.imported}</strong></span>
                <span>Sovrascritte: <strong>{report.totale.updated}</strong></span>
                <span>Saltate: <strong>{report.totale.skipped}</strong></span>
                <span>Errori: <strong className={report.totale.errors ? "text-red-400" : ""}>{report.totale.errors}</strong></span>
              </div>
              <div className="flex justify-end"><Button type="button" variant="outline" onClick={resetJson} className="border-slate-700 text-slate-200 hover:bg-slate-800">Nuova importazione</Button></div>
            </div>
          )}
        </div>

        {/* --- Uffici Reclami PDF --- */}
        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-5 space-y-4" data-testid="import-reclami-card">
          <div className="flex items-center gap-2 text-slate-100 font-head font-semibold">
            <FileText className="h-5 w-5 text-cyan-400" /> Importa Uffici Reclami da PDF
          </div>
          <p className="text-xs text-slate-400">Carica un PDF (es. elenco IVASS degli uffici reclami). Il sistema estrae i dati e li abbina alle compagnie in archivio per ragione sociale / codice ANIA.</p>
          <label className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-600 bg-slate-900/40 px-4 py-6 cursor-pointer hover:border-cyan-500/50 transition-colors" data-testid="reclami-file-label">
            {pParsing ? <Loader2 className="h-6 w-6 text-slate-400 animate-spin" /> : <Upload className="h-6 w-6 text-slate-400" />}
            <span className="text-sm text-slate-300">{pParsing ? "Analisi PDF in corso…" : (pdfName || "Seleziona un file .pdf")}</span>
            <input type="file" accept=".pdf,application/pdf" onChange={onPdf} disabled={pParsing} className="hidden" data-testid="reclami-file-input" />
          </label>

          {recs && !pReport && (
            <>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-slate-300">{recs.length} record estratti</span>
                <span className="text-emerald-300 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />{matchedCount} abbinati</span>
                <span className="text-amber-300 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{unmatchedCount} da rivedere</span>
              </div>
              <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="reclami-review-list">
                {recs.map((r, i) => (
                  <div key={i} className={`rounded-lg border p-2.5 ${mapping[i] ? "border-slate-700 bg-slate-900/40" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`reclami-row-${i}`}>
                    <div className="text-sm font-medium text-slate-100 truncate">{r.ragione_sociale || "—"}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {[r.indirizzo, r.numero_civico, r.cap, r.comune, r.provincia].filter(Boolean).join(" ")}
                      {r.email ? ` · ${r.email}` : ""}{r.pec ? ` · PEC: ${r.pec}` : ""}
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="text-[11px] text-slate-500 shrink-0">Compagnia:</span>
                      <select
                        value={mapping[i] || ""}
                        onChange={(e) => setMapping((m) => ({ ...m, [i]: e.target.value }))}
                        data-testid={`reclami-select-${i}`}
                        className="flex-1 h-8 px-2 rounded-md bg-slate-900/60 border border-slate-700 text-xs text-slate-100 focus:outline-none focus:border-cyan-500/50">
                        <option value="">— Ignora —</option>
                        {(r.candidates || []).length > 0 && (
                          <optgroup label="Suggerite">
                            {r.candidates.map((c) => <option key={`c-${c}`} value={c}>{c}</option>)}
                          </optgroup>
                        )}
                        <optgroup label="Tutte le compagnie">
                          {compList.map((c) => <option key={c} value={c}>{c}</option>)}
                        </optgroup>
                      </select>
                      {r.match && mapping[i] === r.match && <span className="text-[10px] text-emerald-400 shrink-0">auto {Math.round((r.score || 0) * 100)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
              <StudiSelector tenants={tenants} all={pAll} setAll={setPAll} selected={pSelected} setSelected={setPSelected} testPrefix="reclami" />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetPdf} className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
                <Button type="button" onClick={doApplyPdf} disabled={pBusy || matchedCount === 0} data-testid="reclami-apply" className="bg-cyan-500 hover:bg-cyan-600 text-[#0a0d14] font-semibold">
                  {pBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Aggiorna ${matchedCount} uffici`}
                </Button>
              </div>
            </>
          )}

          {pReport && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm space-y-2" data-testid="reclami-report">
              <p className="text-emerald-300 font-semibold">Aggiornati {pReport.updated} record ufficio reclami</p>
              {pReport.not_found?.length > 0 && (
                <div className="text-xs text-amber-300">
                  Non abbinati: {pReport.not_found.join(", ")}
                </div>
              )}
              <div className="flex justify-end"><Button type="button" variant="outline" onClick={resetPdf} className="border-slate-700 text-slate-200 hover:bg-slate-800">Nuova importazione</Button></div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-4 items-start" data-testid="tinv-section">
        <TabelleInvaliditaCard tipo="micro" tabelle={tinv.filter((t) => t.tipo === "micro")} reload={loadTinv} />
        <TabelleInvaliditaCard tipo="macro" tabelle={tinv.filter((t) => t.tipo === "macro")} reload={loadTinv} />
      </div>
    </div>
  );
}
